// @givmo/compliance — the filing workbench's pure model (CCM-R1-DOCS, web half; slice 2's frames
// 30–35, 37 and 49, DESIGN-NOTE-2 §4–§5, approved as shown, D-CCM-22).
//
// Every word below is one Jonathan approved — on slice 2's frames, in NEW-STRINGS-FOR-APPROVAL.md
// (its words-only block included), in an earlier ruling, or as the built panel already says it —
// or one the backend serves. A composition fills an approved template from served facts ("as of
// August 2026" from a source's own date); it adds no word. ⚠ No sentence here states a legal
// conclusion, a filing status the record does not hold, or that anything was sent or accepted.

import {
  displayComplianceDate,
  displayLocalDate,
  isReceiptBacked,
  type ComplianceObligation,
} from './compliance-decode';
import type { RenewalCycle } from './compliance-filings-decode';
import type { NextDate } from './compliance-filings-model';
import { longComplianceDate } from './compliance-panel-model';
import { N12 } from './desk-words';
import type {
  ChecklistItem,
  DocumentVersion,
  FilingCase,
  FormAnswer,
  FormGroup,
  FormItem,
  FormPage,
  FormPreparation,
  FormQuote,
  FormSource,
  Rect,
  SigningRequestRow,
} from './compliance-preparation-decode';

// ── Words (approved: slice 2, D-CCM-22, unless marked) ─────────────────────────────────────────

/** The panel's action (frame 30) and its quiet line. */
export const PREPARE_THE_FORM = 'Prepare the form';
export const PREPARE_LINE =
  'Givmo fills it from your records. Nothing is signed or sent until you choose.';
/** The panel's section once preparation has started (frame 49), and its door to the workbench. */
export const THIS_YEARS_FILING = 'This year’s filing';
export const THE_FILING = 'The filing';
export const OPEN_THE_FORM = 'Open the form';
export const NOT_YET = 'Not yet';
export const NOTHING_RECORDED_YET = 'Nothing recorded yet';
/** The workbench's sections. */
export const THE_FORM = 'The form';
export const WHAT_GOES_WITH_IT = 'What goes with it';
export const DOWNLOAD = 'Download';
/** Under the page: what Download hands over (frames 31–37). */
export const DOWNLOAD_NOTE =
  'Download gives you this exact PDF, as prepared and not signed. Nothing is added to the form.';
export const LOCKED_LINE = 'The answers are locked while the form is out for signature.';
export const AI_OFF_LINE =
  'AI is off for your organization, so Givmo fills this form only from your records and your answers.';
export const NOT_ANSWERED_YET = 'Not answered yet';
export const RECORDS_DIFFER = 'Two of your records differ. Choose what goes on the form.';
export const USE_THIS = 'Use this';
export const DOESNT_APPLY = 'Doesn’t apply';
export const PRINTED_ON_THE_FORM = 'Printed on the form.';
export const PRINTED_ATTACHMENTS_CLAUSE = 'What goes with it, below, shows what’s attached.';
/** A month-and-year item's two blanks, as the form prints them under the line (frames 31 and 32). */
export const MONTH_YEAR_CAPTION = 'Month · Year';
/** The signature block's row (frame 31), for a form whose adapter declares signature places. */
export const SIGNATURE_BLOCK_LABEL = 'Signature, date, printed name and title';
export const SIGNATURE_BLOCK_LINE =
  'Filled in when it’s signed: the signature, the signer’s printed name and title, and the date.';
export const NOT_ATTACHED_YET = 'Not attached yet';
export const CHOOSE_FROM_DOCUMENTS = 'Choose from Documents';
export const UPLOAD = 'Upload';
export const ATTACH = 'Attach';
export const NOT_NEEDED_FOR_THIS_FILING = 'Not needed for this filing';
export const UNDO = 'Undo';
export const CHANGE = 'Change';
export const REMOVE = 'Remove';
export const SAVE = 'Save';
export const CANCEL = 'Cancel';
/** The three-line items' placeholder (frame 34). */
export const NAME_AND_ADDRESS = 'Name and address';
/** The Choose sheet's columns (approved frames 06, 12, 33). */
export const DOCUMENT_COLUMN = 'Document';
export const ADDED_COLUMN = 'Added';
export const USED_FOR_COLUMN = 'Used for';

/** "Maryland’s instructions: “…”" — the approved prefix over the form's own words. */
export function instructionLine(jurisdiction: string, text: string): string {
  return `${jurisdiction}’s instructions: “${text}”`;
}

/** "Maryland’s checklist asks for these with the form." */
export function checklistLead(jurisdiction: string): string {
  return `${jurisdiction}’s checklist asks for these with the form.`;
}

/** "Maryland doesn’t require this one." — an item the form itself excepts (FAX, frame 34). */
export function notRequiredLine(jurisdiction: string): string {
  return `${jurisdiction} doesn’t require this one.`;
}

/** "N/A goes on the form, since Maryland asks for an answer to every item." */
export function naLine(jurisdiction: string): string {
  return `N/A goes on the form, since ${jurisdiction} asks for an answer to every item.`;
}

/** The panel's four lines' labels (frame 49), the jurisdiction named in two. */
export function ledgerLabels(jurisdiction: string): readonly [string, string, string, string] {
  return ['Prepared', 'Signed', `Sent to ${jurisdiction}`, `${jurisdiction}’s confirmation`];
}

/** "1 needs your input." / "2 need your input." (approved frame 14's phrase, with its stop). */
export function inputCountLine(count: number): string {
  return count === 1 ? '1 needs your input.' : `${count} need your input.`;
}

// ── Who and when ───────────────────────────────────────────────────────────────────────────────

type PersonName = ((userId: string) => string | undefined) | undefined;

function nameOf(personName: PersonName, userId: string | undefined): string | undefined {
  return userId !== undefined ? personName?.(userId) : undefined;
}

/**
 * Who made a row: N12, "Givmo’s filing desk", where the row's marker says the desk made it
 * (CCM-R1-DESK-WEB, Q7; D-CCM-70's markers), else the person, as before.
 */
export function makerName(
  personName: PersonName,
  userId: string | undefined,
  byGivmoDesk: boolean | undefined,
): string | undefined {
  return byGivmoDesk === true ? N12 : nameOf(personName, userId);
}

/** "Nov 16, 2026, 10:42 AM" in the viewer's own zone: when someone did something. */
export function displayLocalDateTime(iso: string | undefined, timeZone?: string): string {
  if (iso === undefined) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone !== undefined ? { timeZone } : {}),
  });
  return `${displayLocalDate(iso, timeZone)}, ${time}`;
}

/** "by Maya Solberg on Nov 16, 2026", or "on Nov 16, 2026" for someone the roster cannot name
 * (copy ruling item 8: an unnamed person's line drops the name, never guesses one). */
function byOn(
  personName: PersonName,
  userId: string | undefined,
  at: string | undefined,
  timeZone?: string,
  byGivmoDesk?: boolean,
): string {
  const name = makerName(personName, userId, byGivmoDesk);
  const on = displayLocalDate(at, timeZone);
  return name !== undefined ? `by ${name} on ${on}` : `on ${on}`;
}

/** The workbench's first status line (frames 31–37): when the answers last changed, and by whom. */
export function preparedLine(
  preparation: FormPreparation,
  personName: PersonName,
  timeZone?: string,
): string {
  let at = preparation.createdAt;
  let by = preparation.createdByUserId;
  let byDesk = preparation.createdByGivmoDesk;
  for (const item of preparation.items) {
    const answeredAt = item.answer?.answeredAt;
    if (answeredAt !== undefined && (at === undefined || answeredAt > at)) {
      at = answeredAt;
      by = item.answer?.answeredByUserId;
      byDesk = item.answer?.answeredByGivmoDesk;
    }
  }
  const name = makerName(personName, by, byDesk);
  const when = displayLocalDateTime(at, timeZone);
  return name !== undefined
    ? `Prepared from your records and answers · last changed ${when}, by ${name}`
    : `Prepared from your records and answers · last changed ${when}`;
}

// ── Where a value came from ────────────────────────────────────────────────────────────────────

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "August 2026" from a source's own date (`2026-08-01`), or undefined when unreadable. */
export function monthYearOf(iso: string | undefined): string | undefined {
  const match = iso !== undefined ? /^(\d{4})-(\d{2})/.exec(iso) : null;
  if (match === null) return undefined;
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? `${MONTHS[month - 1]} ${match[1]}` : undefined;
}

/** The facts a source line names beyond the source itself. */
export interface SourceContext {
  /** The organization's fiscal year end as the Compliance profile records it ("June 30"). */
  fiscalYearEnd?: string;
  /** The end of the period the case reports, a civil date (`2026-06-30`). */
  periodEnd?: string;
}

/** "June 30, 2026" for a civil date. */
function longCivilDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return displayComplianceDate(iso);
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
}

/** One quiet line saying which record a value came from, in the frames' words. */
export function sourceLine(source: FormSource, context: SourceContext = {}): string {
  switch (source.kind) {
    case 'ein_on_file':
      return 'From the EIN Givmo has on file for your organization';
    case 'organization_profile':
      return source.field === 'mission'
        ? 'From your organization’s profile (your mission)'
        : 'From your organization’s profile';
    case 'irs_exempt_list': {
      const asOf = monthYearOf(source.asOf);
      return asOf !== undefined
        ? `From the IRS’s list of exempt organizations, as of ${asOf}`
        : 'From the IRS’s list of exempt organizations';
    }
    case 'compliance_profile':
      return context.fiscalYearEnd !== undefined
        ? `From your fiscal year end (${context.fiscalYearEnd})`
        : 'From your fiscal year end';
    case 'filing_cycle': {
      const fye =
        context.fiscalYearEnd !== undefined
          ? `From your fiscal year end (${context.fiscalYearEnd})`
          : 'From your fiscal year end';
      return context.periodEnd !== undefined
        ? `${fye}. This filing covers the year ended ${longCivilDate(context.periodEnd)}`
        : fye;
    }
  }
}

/** The short name a chosen record goes by in "Chosen by … · from …" (frame 41, item 4). */
function chosenFrom(source: FormSource): string | undefined {
  switch (source.kind) {
    case 'irs_exempt_list':
      return 'from the IRS’s list of exempt organizations';
    case 'organization_profile':
      return 'from your organization’s profile';
    default:
      return undefined;
  }
}

/** A text's word count, as frame 34 counts the mission against the form's 25-word guidance. */
export function wordCount(text: string): number {
  return text.split(/\s+/).filter((word) => word.length > 0).length;
}

/** "55 words", as frame 34 puts it after the mission's source. */
export function wordCountLine(text: string): string {
  const count = wordCount(text);
  return `${count} ${count === 1 ? 'word' : 'words'}`;
}

/**
 * A text from one record that the form spreads over several lines (frame 34's purpose: the
 * mission), shown as the record's own paragraph: the filler wrapped that same text onto the form's
 * lines. `undefined` for anything else (an answer, several records, lines that are separate entries).
 */
export function recordParagraph(item: FormItem): string | undefined {
  if (item.kind !== 'lines' || item.state !== 'from_record' || item.answer !== undefined) {
    return undefined;
  }
  if (item.sources.length !== 1) return undefined;
  const text = item.sources[0]?.value;
  return text !== undefined && text.trim().length > 0 ? text : undefined;
}

/**
 * The quiet line under an answered item (frames 31–37): who entered or chose it and when, with
 * "Doesn’t apply" and "Same as the mailing address" where the answer is one of those.
 */
export function answeredLine(
  item: FormItem,
  answer: FormAnswer,
  personName: PersonName,
  sameAsLabel: string | undefined,
  timeZone?: string,
): string {
  const who = byOn(
    personName,
    answer.answeredByUserId,
    answer.answeredAt,
    timeZone,
    answer.answeredByGivmoDesk,
  );
  switch (answer.kind) {
    case 'doesnt_apply':
      return `${DOESNT_APPLY} · entered ${who}`;
    case 'same_as':
      return sameAsLabel !== undefined && /mailing address/i.test(sameAsLabel)
        ? `Same as the mailing address · entered ${who}`
        : `Entered ${who}`;
    case 'choose_source': {
      const chosen = item.sources[0];
      const from = chosen !== undefined ? chosenFrom(chosen) : undefined;
      return from !== undefined ? `Chosen ${who} · ${from}` : `Chosen ${who}`;
    }
    default:
      return `Entered ${who}`;
  }
}

// ── The page column ────────────────────────────────────────────────────────────────────────────

/** The pages that hold an item: the form itself, as opposed to its instructions. */
export function formPagesOf(preparation: FormPreparation): number[] {
  const pages = new Set<number>();
  for (const item of preparation.items) for (const place of item.places) pages.add(place.page);
  return [...pages].sort((a, b) => a - b);
}

/** Every other page of the agency's document: its instructions, which stay in the PDF (Q5). */
export function instructionPagesOf(preparation: FormPreparation): number[] {
  const form = new Set(formPagesOf(preparation));
  return preparation.pages.map((page) => page.page).filter((page) => !form.has(page));
}

/** "pages 1–3", or "page 2" for one. */
export function pagesPhrase(pages: readonly number[]): string {
  if (pages.length === 1) return `page ${pages[0]}`;
  const sorted = [...pages].sort((a, b) => a - b);
  const contiguous = sorted.every((page, index) => index === 0 || page === sorted[index - 1]! + 1);
  return contiguous
    ? `pages ${sorted[0]}–${sorted[sorted.length - 1]}`
    : `pages ${sorted.join(', ')}`;
}

/** Where a place lands on its page's image, as percentages of the drawn page. */
export interface PlaceBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A rectangle in PDF points (from the page's bottom left) placed on the drawn page: the preview
 * draws the page's crop box at one uniform scale, so a place is the same FRACTION of the image
 * whatever width it is shown at — x from the box's left edge, y from its TOP edge (PDF space runs
 * upward, the image downward). A rotated page is not mapped: no outline beats a wrong one.
 */
export function placeBox(rectangle: Rect, page: FormPage): PlaceBox | undefined {
  if (page.rotation % 360 !== 0) return undefined;
  const [x0, y0, x1, y1] = rectangle;
  const [cx0, cy0, cx1, cy1] = page.cropBox;
  const boxWidth = cx1 - cx0;
  const boxHeight = cy1 - cy0;
  if (boxWidth <= 0 || boxHeight <= 0) return undefined;
  return {
    left: ((x0 - cx0) / boxWidth) * 100,
    top: ((cy1 - y1) / boxHeight) * 100,
    width: ((x1 - x0) / boxWidth) * 100,
    height: ((y1 - y0) / boxHeight) * 100,
  };
}

// ── The checklist ──────────────────────────────────────────────────────────────────────────────

/**
 * "Chosen by Maya Solberg · in Documents since Nov 12, 2026" for one attached file, by its newest
 * version: Givmo's filing desk where the desk made it (N12).
 */
export function chosenFileLine(
  version: Pick<DocumentVersion, 'createdByUserId' | 'createdByGivmoDesk'> | undefined,
  inDocumentsSince: string | undefined,
  personName: PersonName,
  timeZone?: string,
): string {
  const name = makerName(personName, version?.createdByUserId, version?.createdByGivmoDesk);
  const parts = [name !== undefined ? `Chosen by ${name}` : 'Chosen'];
  if (inDocumentsSince !== undefined) {
    parts.push(`in Documents since ${displayLocalDate(inDocumentsSince, timeZone)}`);
  }
  return parts.join(' · ');
}

/** "Not needed for this filing · marked by Maya Solberg on Nov 16, 2026". */
export function notNeededLine(
  item: ChecklistItem,
  personName: PersonName,
  timeZone?: string,
): string {
  const act = item.lastAct;
  if (act === undefined || act.act !== 'not_needed') return NOT_NEEDED_FOR_THIS_FILING;
  return `${NOT_NEEDED_FOR_THIS_FILING} · marked ${byOn(
    personName,
    act.recordedByUserId,
    act.recordedAt,
    timeZone,
    act.recordedByGivmoDesk,
  )}`;
}

// ── Prepare, and the case it is prepared in ────────────────────────────────────────────────────

/**
 * The case a "Prepare the form" opens (or finds): the panel's own record target — the year the
 * Next due names (a durable cycle: a `renewal` case on it), or a duty made once (an `initial`
 * case, no cycle). `kind` is the organization's own statement (contract §6.1), never checked.
 */
export type PreparationTarget = { kind: 'renewal'; cycle: RenewalCycle } | { kind: 'initial' };

/**
 * The target a filing's next date names, where the panel offers its action: the dated year's
 * durable cycle (a `renewal` case), else the filing as a whole (an `initial` case). A next date
 * with no action (a date the organization still owes, a filing already filed once, one no longer
 * tracked) prepares nothing.
 */
export function preparationTargetFor(next: NextDate): PreparationTarget | undefined {
  switch (next.kind) {
    case 'dated':
      return next.cycle !== undefined
        ? { kind: 'renewal', cycle: next.cycle }
        : { kind: 'initial' };
    case 'before-first-solicitation':
    case 'not-supported':
      return { kind: 'initial' };
    default:
      return undefined;
  }
}

/** An item as the form prints it: its number, then its words ("9. County:"). */
export function itemDisplayLabel(item: FormItem): string {
  return item.number.length > 0 ? `${item.number} ${item.label}` : item.label;
}

// ── What the form prints beside its items (CCM-R1-DOCS-PRINTED-WEB; frames 31, 34, 60, 61, 67) ──

/** The form's identity as a screen names it: its number and, where served, its printed title. */
export interface FormNaming {
  formNumber: string;
  printedTitle?: FormQuote;
}

/** "Form COR-92" (frames 34 and 61's subtitle; frames 60 and 67's panel line). */
export function formNumberLine(formNumber: string): string {
  return `Form ${formNumber}`;
}

/** The title a form is shown under: the title it prints, else its number. */
export function formTitle(form: FormNaming): string {
  return form.printedTitle?.text ?? form.formNumber;
}

/**
 * Whether the form prints a title other than its number (COR-92), so the number is named beside it.
 * The Annual Update's number IS its printed title, and is never said twice.
 */
export function namesItsNumber(form: FormNaming): boolean {
  const title = form.printedTitle?.text.trim();
  return title !== undefined && title.length > 0 && title !== form.formNumber.trim();
}

function lowerFirst(text: string): string {
  return text.length > 0 ? `${text.charAt(0).toLowerCase()}${text.slice(1)}` : text;
}

/**
 * The workbench's subtitle under the printed title: "Form COR-92 · before you first solicit in
 * Maryland" (frames 34, 61), or, for a form whose number is its title, the line alone ("For the
 * fiscal year ended June 30, 2026", frame 31). `when` is the line the page composes for the period
 * or the first solicitation, as the panel words it.
 */
export function workbenchSubtitle(form: FormNaming, when: string | undefined): string | undefined {
  if (!namesItsNumber(form)) return when;
  const number = formNumberLine(form.formNumber);
  return when !== undefined ? `${number} · ${lowerFirst(when)}` : number;
}

/**
 * The panel's line under "The filing" / "This year’s filing": the printed title, its number where
 * that differs, and a year-keyed cycle's period ("Registration Statement for Charitable
 * Organizations · Form COR-92", frames 60 and 67; "Annual Update of Registration Form · fiscal year
 * ended June 30, 2026", frame 49).
 */
export function filingSubtitle(form: FormNaming, periodEnd?: string): string {
  const parts = [formTitle(form)];
  if (namesItsNumber(form)) parts.push(formNumberLine(form.formNumber));
  if (periodEnd !== undefined) parts.push(`fiscal year ended ${longComplianceDate(periodEnd)}`);
  return parts.join(' · ');
}

/** "See the checklist (page 2)" (frames 31, 32, 61), its page the form's own. */
export function seeTheChecklistLine(page: number): string {
  return `See the checklist (page ${page})`;
}

/**
 * APPROVED (D-CCM-108, Jonathan 2026-10-18 07:46 PDT, "approve recs"; REVIEW-47's choice 6): after a
 * figure the form prints malformed ("$50,0001"), so the misprint reads as the form's own, never as
 * Givmo's. The cell stays exactly as printed.
 */
export const AS_PRINTED = '(as printed)';

/**
 * Whether a fee chart cell prints a dollar figure whose digit groups are malformed: a figure with
 * thousands separators where a group isn't three digits. A figure with no separator is well formed.
 */
export function printsAMalformedFigure(cell: string): boolean {
  return (cell.match(/\$[\d,]+/g) ?? []).some((figure) => {
    const bare = figure.replace(/,+$/, '');
    return !/^\$(\d{1,3}(,\d{3})+|\d+)$/.test(bare);
  });
}

/** "Maryland’s fee chart, from page 2 of the form" (frame 34), its page the form's own. */
export function feeChartCaption(jurisdiction: string, page: number): string {
  return `${jurisdiction}’s fee chart, from page ${page} of the form`;
}

/**
 * A fee chart note as frame 34 draws it: one that opens with the footnote mark a row carries ("*A
 * charitable organization …", keyed to "Less Than $25,000 (see note below)*") is the chart's own
 * footnote, as printed; any other is the form's sentence, in quotes.
 */
export function feeChartNoteLine(text: string, rows: readonly (readonly string[])[]): string {
  const mark = text.charAt(0);
  const footnote =
    mark.length > 0 &&
    !/[\p{L}\p{N}\s"“]/u.test(mark) &&
    rows.some((row) => row.some((cell) => cell.includes(mark)));
  return footnote ? text : `“${text}”`;
}

/** The item a form's fee chart sits under: the fee, by the key the backend reads it by. */
export const FEE_ITEM_KEY = 'fee';

/** A choice's label as a group's value line leads with it: its own trailing colon dropped. */
export function bareLabel(label: string): string {
  return label.replace(/\s*:\s*$/, '');
}

/** "Professional Solicitor: No · Fund-raising Counsel: No" (frames 31, 61). */
export function groupValueLine(parts: readonly { label: string; value: string }[]): string {
  return parts.map((part) => `${bareLabel(part.label)}: ${part.value}`).join(' · ');
}

/**
 * A group's quiet lines under its value (frames 31, 61): one line where every item's is the same
 * ("Entered by Maya Solberg on Nov 16, 2026"); otherwise one per item, led by its label
 * ("Professional Solicitor: entered by …").
 */
export function groupSourceLines(lines: readonly { label: string; line: string }[]): string[] {
  if (lines.length === 0) return [];
  if (lines.every((entry) => entry.line === lines[0]!.line)) return [lines[0]!.line];
  return lines.map((entry) => `${bareLabel(entry.label)}: ${lowerFirst(entry.line)}`);
}

/** What the workbench draws, in the form's order: an item, or a question the form prints over several. */
export type WorkbenchUnit =
  | { kind: 'item'; item: FormItem }
  | { kind: 'group'; group: FormGroup; items: FormItem[] };

/**
 * The drawn items as the form prints them: each group once, at its first drawn item's place, holding
 * its drawn items in the group's order; every other item on its own. An item the form doesn't ask
 * yet is not among `items`, so a group is drawn wherever any of its items is.
 */
export function workbenchUnits(
  items: readonly FormItem[],
  groups: readonly FormGroup[],
): WorkbenchUnit[] {
  const groupOf = new Map<string, FormGroup>();
  for (const group of groups) for (const key of group.itemKeys) groupOf.set(key, group);
  const units: WorkbenchUnit[] = [];
  const placed = new Set<FormGroup>();
  for (const item of items) {
    const group = groupOf.get(item.key);
    if (group === undefined) {
      units.push({ kind: 'item', item });
      continue;
    }
    if (placed.has(group)) continue;
    placed.add(group);
    const members = group.itemKeys.flatMap((key) => {
      const member = items.find((candidate) => candidate.key === key);
      return member !== undefined ? [member] : [];
    });
    units.push({ kind: 'group', group, items: members });
  }
  return units;
}

/** The open case already holding this target's filing, if one is open. */
export function caseForTarget(
  cases: readonly FilingCase[],
  target: PreparationTarget,
): FilingCase | undefined {
  const open = cases.filter((filingCase) => filingCase.open);
  if (target.kind === 'renewal') {
    return open.find((filingCase) => filingCase.renewalCycleId === target.cycle.cycleId);
  }
  return open.find(
    (filingCase) => filingCase.renewalCycleId === undefined && filingCase.kind === 'initial',
  );
}

/** The body that opens the target's case. */
export function openCaseBody(target: PreparationTarget): {
  kind: 'renewal' | 'initial';
  renewal_cycle_id?: string;
} {
  return target.kind === 'renewal'
    ? { kind: 'renewal', renewal_cycle_id: target.cycle.cycleId }
    : { kind: 'initial' };
}

// ── The panel's four lines (frame 49) ──────────────────────────────────────────────────────────

export interface LedgerFacts {
  preparation: FormPreparation | undefined;
  /** The case's newest request that is open or completed, if any. */
  request: SigningRequestRow | undefined;
  /** Who is named on the open request's places, or who signed the completed one. */
  signerNames: readonly string[];
  /** The completed request's last signature's instant (it completes the request). */
  signedAt?: string;
  /** The register's own record for the target: the year's cycle, or the duty made once. */
  cycle?: RenewalCycle;
  obligation: ComplianceObligation;
}

/**
 * Who prepared what a recorded version carries: the person whose answer is the newest at or before
 * the version was recorded, else whoever started the form. Never the person who RECORDED it: a
 * Download records one at READ (CCM-R1-DOCS-READS B2), so a viewer who only downloaded is never
 * named as the preparer.
 */
function preparerOf(
  preparation: FormPreparation,
  recordedAt: string,
): { userId: string | undefined; byGivmoDesk: boolean | undefined } {
  const recorded = Date.parse(recordedAt);
  let newest: number | undefined;
  let by = preparation.createdByUserId;
  let byDesk = preparation.createdByGivmoDesk;
  for (const item of preparation.items) {
    const answer = item.answer;
    const at = answer?.answeredAt !== undefined ? Date.parse(answer.answeredAt) : Number.NaN;
    if (Number.isNaN(at) || (!Number.isNaN(recorded) && at > recorded)) continue;
    if (newest === undefined || at > newest) {
      newest = at;
      by = answer?.answeredByUserId;
      byDesk = answer?.answeredByGivmoDesk;
    }
  }
  return { userId: by, byGivmoDesk: byDesk };
}

/**
 * The four lines, each from its own record, so a signature never reads as sent or confirmed
 * (DESIGN-NOTE-2 §4): Prepared from the preparation and its newest version (the version's date,
 * and who prepared what it carries); Signed from the signing request; Sent from the register's
 * sent record; Confirmation from a receipt-backed filing record only.
 */
export function ledgerValues(
  facts: LedgerFacts,
  personName: PersonName,
  timeZone?: string,
): string[] {
  // Prepared is a RECORDED version (Download or Send records one), never the preparation's start;
  // the name is the preparer's, never the recorder's.
  const prepared = ((): string => {
    const preparation = facts.preparation;
    const at = preparation?.latestVersion?.createdAt;
    if (preparation === undefined || at === undefined) return NOT_YET;
    const preparer = preparerOf(preparation, at);
    const name = makerName(personName, preparer.userId, preparer.byGivmoDesk);
    return name !== undefined
      ? `${displayLocalDate(at, timeZone)}, by ${name}`
      : displayLocalDate(at, timeZone);
  })();

  const signed = ((): string => {
    const request = facts.request;
    if (request === undefined) return NOT_YET;
    const names = facts.signerNames.join(', ');
    if (request.state === 'completed') {
      const at = facts.signedAt ?? request.completedAt;
      return names.length > 0
        ? `${displayLocalDate(at, timeZone)}, by ${names}`
        : displayLocalDate(at, timeZone);
    }
    if (request.state === 'open') {
      // The sign-by is an instant: the day it falls on for the viewer.
      const by = displayLocalDate(request.expiresAt, timeZone);
      return names.length > 0
        ? `Out for signature with ${names} · sign by ${by}`
        : `Out for signature · sign by ${by}`;
    }
    return NOT_YET;
  })();

  // A year's ledger reads that year's own send (CCM-BE18), never the filing row's: the row's record
  // names no year, so last year's send would read under the next (CCM-R1-PANEL-SENT-YEAR). The row's
  // record serves only the filing as a whole, a duty made once.
  const sentOn =
    facts.cycle !== undefined
      ? facts.cycle.submissionRecordedOn
      : facts.obligation.submissionRecordedOn;
  const sent = sentOn !== undefined ? displayComplianceDate(sentOn) : NOTHING_RECORDED_YET;

  const state = facts.cycle?.filingRecordState ?? facts.obligation.filingRecordState;
  // The same rule for the filed date: a year's confirmation is that year's own, never the row's.
  const filedOn =
    facts.cycle !== undefined ? facts.cycle.recordedFiledOn : facts.obligation.recordedFiledOn;
  const confirmation =
    isReceiptBacked(state) && filedOn !== undefined
      ? `Filed ${displayComplianceDate(filedOn)} · Confirmation`
      : NOTHING_RECORDED_YET;

  return [prepared, signed, sent, confirmation];
}
